import type { ComponentProps } from 'react';
import React, { useCallback } from 'react';
import type { ViewProps } from 'react-native';
import { StyleSheet, View } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import type { Region } from '@sweetgreen/garnish';
import { Modal, theme, useResponsive } from '@sweetgreen/garnish';

import { LoadingAnimation } from '../LoadingAnimation';
import { NotFoundView } from '../NotFoundView';
import { TransparentModal } from '../TransparentModal';
import type {
  DeliveryLocationCardProps,
  RestaurantLocationCardProps,
} from './subcomponents';
import { ConfirmLocationLayout } from './subcomponents';

export const ConfirmLocation = (props: ConfirmLocationProps) => {
  const { isFetching, restaurant, hasError, region, ...restProps } = props;

  if (isFetching) {
    return (
      <ConfirmLocationContainer>
        <View style={styles.notLoadedView}>
          <LoadingAnimation />
        </View>
      </ConfirmLocationContainer>
    );
  }

  if (hasError || !restaurant || !region) {
    return (
      <ConfirmLocationContainer>
        <View style={styles.notLoadedView}>
          <NotFoundView withoutFooter />
        </View>
      </ConfirmLocationContainer>
    );
  }

  return (
    <ConfirmLocationContainer>
      <ConfirmLocationLayout
        region={region}
        restaurant={restaurant}
        {...restProps}
      />
    </ConfirmLocationContainer>
  );
};

const ConfirmLocationContainer = (props: ViewProps) => {
  const { children } = props;

  const { match, minWidth } = useResponsive();
  const navigation = useNavigation();

  const handleDismiss = useCallback(() => {
    if (navigation.canGoBack()) {
      navigation.goBack();

      return;
    }

    navigation.getParent()?.goBack();
  }, [navigation]);

  return (
    <TransparentModal
      testID="confirm-location"
      size={match(['full', 'medium'])}
      onClose={handleDismiss}
      mobileEdges={['right', 'bottom', 'left']}
    >
      {minWidth.tablet ? (
        <Modal.FloatingCloseBtn
          onPress={handleDismiss}
          style={styles.closeBtn}
        />
      ) : null}

      {children}
    </TransparentModal>
  );
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  notLoadedView: {
    flex: 1,
    paddingVertical: theme.spacing.large,
    backgroundColor: theme.colors.OATMEAL,
  },
  closeBtn: {
    marginTop: theme.spacing.large,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type ConfirmLocationProps = {
  readonly isFetching: boolean;
  readonly hasError: boolean;
  readonly region?: Region | null;
  readonly restaurant?: RestaurantLocationCardProps['restaurant'] | null;
  readonly deliveryAddress?:
    | DeliveryLocationCardProps['deliveryAddress']
    | null;
} & Omit<
  ComponentProps<typeof ConfirmLocationLayout>,
  'restaurant' | 'region' | 'deliveryAddress'
>;
